import { Injectable } from '@angular/core';
import { ClassicCard } from '@braze/web-sdk';
import { BehaviorSubject, catchError, filter, map, Observable, of, retry, switchMap, tap, throwError } from 'rxjs';
import { filterCardsBySlot } from 'src/app/shared/utilities/braze-content-cards/card-slot-filter/card-slot-filter';
import { truncateContentCards } from 'src/app/shared/utilities/braze-content-cards/truncate-content-cards/truncate-content-cards';
import { distinctUntilObjectChanged } from 'src/app/shared/utilities/rxjs/distinct-until-object-changed';

type BrazeContentCardFilter = {
  slot: string;
  numberOfCards?: number;
};

declare global {
  // Extend window object to hold Qualtrics object.
  // Type 'any' is used due to no control over the structure of Braze SDK and no typing within the imported package.
  interface Window {
    braze?: any;
  }
}

/**
 * BrazeContentCardsService will deal with loading and hiding Content Cards from Braze.
 *
 * **NOTE**
 * This is a POC service, and the usage of it is in its experimental phase.
 *
 * **Useful Documentation**
 * - https://www.braze.com/docs/developer_guide/platform_integration_guides/web/content_cards/integration/#standard-feed-ui
 * - https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html#getcachedcontentcards
 */
@Injectable({
  providedIn: 'root',
})
export class BrazeContentCardsService {
  /** Used to prevent re initializing the content card subject. */
  private initialized = false;

  /** Content cards the app has to work with from Braze.  */
  private contentCardsSubject: BehaviorSubject<ClassicCard[]> = new BehaviorSubject([]);

  /** Max number of attempts the service would try to initialize content cards before erroring. */
  private MAX_INIT_RETRIES = 5;

  /** The delay in between attempting to verify braze sdk is initialised. */
  private RETRY_DELAY = 5000;

  /** The default number of content cards displayed. */
  private DEFAULT_MAX_CARDS_DISPLAYED = 5;

  /**
   * A list of content cards provided to us from Braze.
   *
   * @deprecated Please use getContentCards$(filter) instead.
   */
  get contentCards$(): Observable<ClassicCard[]> {
    return this.contentCardsSubject.asObservable().pipe(distinctUntilObjectChanged());
  }

  /**
   * Returns an observable stream of content cards fetched from Braze,
   * filtered according to the provided criteria.
   *
   * Read more about filters in https://attainhealthtech.atlassian.net/wiki/spaces/EN/pages/3625975930/Braze+Content+Cards+Filter.
   *
   * @param cardsFilter - An object specifying how to filter the Braze content cards.
   * @returns An observable emitting an array of filtered ClassicCard objects.
   */
  getContentCards$(cardsFilter: BrazeContentCardFilter): Observable<ClassicCard[]> {
    if (!cardsFilter.slot || cardsFilter.slot.trim() === '') {
      return throwError(() => new Error('Braze cards require a slot filter'));
    }

    return this.contentCardsSubject.asObservable().pipe(
      distinctUntilObjectChanged(),

      // Filter the cards by the slot provided.
      map((cards: ClassicCard[]) => filterCardsBySlot(cardsFilter.slot, cards)),

      // If a number of cards is specified, truncate the list to that number.
      truncateContentCards(cardsFilter.numberOfCards || this.DEFAULT_MAX_CARDS_DISPLAYED),

      // Filter out any empty arrays, so we don't emit empty lists.
      filter((cards: ClassicCard[]) => Boolean(cards?.length)),

      // Ensures the cards have impressions logged into Braze.
      // This MUST be the last thing in the chain to ensure only shown cards are logged.
      tap((cards: ClassicCard[]) => this.logContentCardImpressions(cards)),
    );
  }

  /**
   * To be called when the app starts for the first time. Attempts to pull any cards cached in the browser and put
   * them into the `BehaviorSubject` contentCards. It then subscribes to the Braze SDK to listen for any new content
   * cards coming directly from Braze (updating the `BehaviorSubject` contentCards as required).
   *
   * There is no need to unsubscribe as it should be called only once when the app and SDK load, and only destroyed.
   *
   * When a user logs out and in, the SDK is notified from within the Segment service. TODO: figure out how this works
   * with logins and outs of different users and the impact on cards.
   *
   * @returns `true` if content cards have now been loaded, otherwise `false`. False includes scenarios where init is re called.
   */
  init(): Observable<boolean> {
    if (this.initialized) {
      // No need to any duplicate calls for noise, just ignore.
      return of(false);
    }

    return this.isBrazeLoaded$();
  }

  /**
   * Wipes any braze data saved current in the users browser. Specifically will clear cachedContentCards.
   * Useful when users logout and in as someone else.
   */
  wipeData(): void {
    if (this.isBrazeLoadedIntoWindow()) {
      // See: https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html#wipedata
      window.braze.wipeData();
      this.contentCardsSubject.next([]);
    }
  }

  /**
   * Helper method, used to log when a user clicks on the content card.
   *
   * @param card - The Card object that received a click.
   * @returns Whether or not the event was successfully logged (to be flushed later by the Braze SDK).
   */
  logContentCardClick(card: ClassicCard): boolean {
    // https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html#logcontentcardclick
    return window.braze.logContentCardClick(card);
  }

  /**
   * Helper method, used to log that a list of content cards have been viewed.
   * This method is setup if you need to do this manually instead of using the `logContentCardImpressions`
   * RxJs operator.
   *
   * @param cards - Array of Card objects that received impressions.
   * @returns Whether or not the event was successfully logged (to be flushed later by the Braze SDK).
   */
  logContentCardImpressions(cards: ClassicCard[]): boolean {
    // https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html#logcontentcardimpressions
    return window.braze.logContentCardImpressions(cards);
  }

  /**
   * Helper method to make it easier to determine if Braze has loaded into the `window` yet.
   *
   * @returns `true` if loaded in, else `false`.
   */
  private isBrazeLoadedIntoWindow(): boolean {
    return !!window.braze;
  }

  /**
   * Encapsulates the retry logic to attempt to load the Braze SDK. If unsuccessful, an error will be loged and
   * false is returned. If we can verify the SDK is loaded into the window, we attempt to actually load content
   * cards and return true.
   *
   * Retry logic is required since there is no `nice` way to determine once `segment` has sideloaded the Braze SDK.
   * There can be a delay, so firstly we should only try to init the service once segments loaded. Secondly we need
   * basically poll until its also loaded in Braze. This is the `retry` logic.
   *
   * @returns `true` if braze is loaded, `false` if not. If true, content cards will be called and loaded.
   */
  private isBrazeLoaded$(): Observable<boolean> {
    return of({}).pipe(
      switchMap(() => of(this.isBrazeLoadedIntoWindow())),
      // Check to see if the braze sdk has been loaded by segment
      switchMap((loaded) =>
        loaded
          ? of(true)
          : throwError(
              () => new Error('BrazeContentCardsService :: Could not load Content Cards, the Braze SDK is not loaded'),
            ),
      ),

      // Retry if the previous check (braze sdk load) fails. Upon reaching the max retry an error is thrown to the subscribe and the sub ends
      retry({ count: this.MAX_INIT_RETRIES, delay: this.RETRY_DELAY }),

      // After the refresh, make sure we grab those cards
      switchMap(() => this.loadCachedContentCards$()),

      catchError((error) => {
        console.error(error);
        return of(false);
      }),
    );
  }

  /**
   * Fetches any cached content cards from local storage, and sets up a subscription to listen for new cards directly
   * from Braze. Marks the service as initalised.
   *
   * @returns `true` upon completion.
   */
  private loadCachedContentCards$(): Observable<boolean> {
    return of(window.braze.getCachedContentCards()).pipe(
      // Side effects with content cards
      tap((cachedContentCards) => {
        // Mark the content cards service as loaded
        this.initialized = true;

        // Set cached cards into the subject
        if (cachedContentCards && cachedContentCards.cards?.length) {
          this.contentCardsSubject.next(cachedContentCards.cards);
        }

        // Setup a subscription to Braze to receive new cards async. No need to unsub as this should live as long as the session
        window.braze.subscribeToContentCardsUpdates((contentCards) =>
          this.contentCardsSubject.next(contentCards.cards),
        );
      }),

      // Return true for use in parent
      map(() => true),
    );
  }
}
